import { expressApi } from '@/state/api/expressApi';
import { triggerBrowserDownload } from '@/shared/utils/triggerBrowserDownload';
import type {
  PageDto,
  TranslationJobSummaryDto,
  TranslationJobResponseDto,
  RetryTranslationJobRequestDto,
  RetryTranslationJobResponseDto,
} from '@/types';

export interface ListTranslationJobsArgs {
  uploadBatchId: string;
  size?: number;
}

export interface ListTranslationJobsResult {
  data: PageDto<TranslationJobSummaryDto>;
  retryAfter: number | null;
}

export const translateApi = expressApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /translation-jobs?uploadBatchId={id}&size=100 — list translation jobs for a batch.
     * Handles Retry-After header for polling while jobs are still processing.
     */
    listTranslationJobs: builder.query<ListTranslationJobsResult, ListTranslationJobsArgs>({
      query: ({ uploadBatchId, size = 100 }) => ({
        url: '/translation-jobs',
        params: { uploadBatchId, size },
      }),
      transformResponse: (response: PageDto<TranslationJobSummaryDto>, meta) => {
        const retryAfterHeader = meta?.response?.headers.get('Retry-After');
        const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : null;
        return { data: response, retryAfter };
      },
      providesTags: ['TranslationJobs'],
    }),

    /**
     * GET /translation-jobs/{jobId} — get full details for a single translation job.
     * Includes translatedFiles array when completed.
     */
    getTranslationJob: builder.query<TranslationJobResponseDto, string>({
      query: (jobId) => `/translation-jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'TranslationJobs', id: jobId }],
    }),

    /**
     * POST /translation-jobs/{jobId}/retry — retry a failed translation job.
     * Optionally allows specifying a subset of targetLangs to retry.
     */
    retryTranslationJob: builder.mutation<
      RetryTranslationJobResponseDto,
      { jobId: string; body?: RetryTranslationJobRequestDto }
    >({
      query: ({ jobId, body }) => ({
        url: `/translation-jobs/${jobId}/retry`,
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: ['TranslationJobs'],
    }),

    /**
     * GET /translated-files/{translatedFileId} — download a translated file as blob.
     * Triggers a browser download via a temporary <a> element.
     */
    downloadTranslatedFile: builder.mutation<void, { translatedFileId: string; filename: string }>({
      queryFn: async ({ translatedFileId, filename }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/translated-files/${translatedFileId}`,
          // Blob on success, parsed JSON on failure. A blanket `.blob()` also
          // wrapped error bodies, so an RFC 7807 problem arrived as a Blob and
          // the XTM Cloud guard in the base query could not see it — a rejected
          // XTM Cloud password during a download still ended on "access denied".
          responseHandler: async (response) =>
            response.ok ? response.blob() : response.json().catch(() => undefined),
        });

        if (result.error) {
          return { error: result.error };
        }

        triggerBrowserDownload(result.data as Blob, filename);
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useListTranslationJobsQuery,
  useLazyListTranslationJobsQuery,
  useGetTranslationJobQuery,
  useLazyGetTranslationJobQuery,
  useRetryTranslationJobMutation,
  useDownloadTranslatedFileMutation,
} = translateApi;
